/*
 * trautis-fitment.js — spec §9.1.4, §9.2, §9.3, §9.3a
 *
 * Public API on window.TrautisFitment (browser) / module.exports (Node):
 *   STORAGE_KEY         constant — 'trautis.mein_bmw'
 *   get()               returns the stored bmw object or null
 *   set(bmw)            persists + dispatches 'trautis:bmw-changed'
 *   clear()             removes + dispatches 'trautis:bmw-changed' with null detail
 *
 * Subsequent Phase 1d tasks layer in: parseBmwParam, serializeBmwParam,
 * hydrateFromQuery, loadLookup, resolveFilterIds, appendFilterToUrl,
 * installNavInterceptor, matches, init.
 */
(function (root) {
  'use strict';

  var STORAGE_KEY = 'trautis.mein_bmw';
  var EVENT_NAME = 'trautis:bmw-changed';

  // Sentinel für „Alle Motoren" (Selektor-Umbau 2026-06-07). In Storage/URL
  // steht 'ALL'; kundensichtbar übersetzt engineDisplay() auf Deutsch.
  var ALL_ENGINE = 'ALL';

  function engineDisplay(engine) {
    return engine === ALL_ENGINE ? 'alle Motoren' : engine;
  }

  // In-Memory-Fallback für blockierten/vollen localStorage (Private-Mode,
  // Storage-Policies): Schreiben darf die Fahrzeugwahl-UI nie crashen. Nach
  // einem fehlgeschlagenen Write liest get() aus dem Speicher-Spiegel, damit
  // Event-Konsumenten und direkte get()-Aufrufer dieselbe Sicht haben —
  // Persistenz über den Seitenwechsel hinaus gibt es dann schlicht nicht.
  var memoryBmw = null;
  var storageBroken = false;

  function get() {
    if (storageBroken) return memoryBmw;
    try {
      var raw = root.localStorage.getItem(STORAGE_KEY);
      if (raw == null) return null;
      return JSON.parse(raw);
    } catch (e) {
      return memoryBmw;
    }
  }

  function dispatchChange(detail) {
    var ev = new root.CustomEvent(EVENT_NAME, { detail: detail });
    (root.document || root).dispatchEvent(ev);
  }

  function set(bmw) {
    memoryBmw = bmw;
    try {
      root.localStorage.setItem(STORAGE_KEY, JSON.stringify(bmw));
      storageBroken = false;
    } catch (e) {
      storageBroken = true;
    }
    dispatchChange(bmw);
  }

  function clear() {
    memoryBmw = null;
    try {
      root.localStorage.removeItem(STORAGE_KEY);
      storageBroken = false;
    } catch (e) {
      storageBroken = true;
    }
    dispatchChange(null);
  }

  // --- Deep-link param ---

  function parseBmwParam(str) {
    if (typeof str !== 'string') return null;
    var s = str.trim().toUpperCase();
    var m = s.match(/^([EFG][0-9]{1,3})-(ALL|[BMNPS][0-9]{2,3}[A-Z]?)$/);
    if (!m) return null;
    return { chassis: m[1], engine: m[2] };
  }

  function serializeBmwParam(bmw) {
    if (!bmw || !bmw.chassis || !bmw.engine) return '';
    return bmw.chassis + '-' + bmw.engine;
  }

  function hydrateFromQuery() {
    try {
      var url = new URL(root.location.href);
      var raw = url.searchParams.get('bmw');
      if (!raw) return;
      var parsed = parseBmwParam(raw);
      if (!parsed) return;
      // Hydrate state — preserve any existing label/lci_state if already set,
      // otherwise build a minimal label from chassis+engine.
      var existing = get();
      var hydrated = {
        chassis: parsed.chassis,
        engine: parsed.engine,
        label: (existing && existing.chassis === parsed.chassis && existing.engine === parsed.engine && existing.label)
          ? existing.label
          : (parsed.chassis + ' · ' + parsed.engine),
      };
      set(hydrated);
      // Strip ?bmw= so subsequent nav URLs stay clean
      url.searchParams.delete('bmw');
      var clean = url.pathname + (url.searchParams.toString() ? '?' + url.searchParams.toString() : '') + url.hash;
      root.history.replaceState({}, '', clean);
    } catch (e) {
      // URL parsing failures should not crash the page
    }
  }

  // --- Lookup (chassis+engine → fitment_application GIDs) ---

  var LOOKUP_URL_FN = null;   // resolved at init() time from data attribute
  var lookupPromise = null;

  function loadLookup() {
    if (lookupPromise) return lookupPromise;
    var url = (typeof LOOKUP_URL_FN === 'function') ? LOOKUP_URL_FN() : '/cdn/shop/t/1/assets/trautis-fitment-lookup.json';
    lookupPromise = root.fetch(url).then(function (res) {
      if (!res.ok) throw new Error('lookup fetch failed: ' + res.status);
      return res.json();
    });
    return lookupPromise;
  }

  function resolveFilterIds(chassis, engine) {
    return loadLookup().then(function (data) {
      if (!data || !data.byChassisEngine) return [];
      var byChassis = data.byChassisEngine[chassis];
      if (!byChassis) return [];
      // Exakter Motor PLUS chassis-weiter "any"-Eintrag — motorunabhängige Teile
      // tragen die any-GID. ALL = Union ALLER Keys des Chassis (inkl. any), für
      // die „Alle Motoren"-Auswahl. Motor ohne Lookup-Daten ⇒ nur any-GIDs
      // (Spec-Entscheidung 3). Dedupe erhält Reihenfolge.
      var keys = (engine === ALL_ENGINE) ? Object.keys(byChassis) : [engine, 'any'];
      var out = [];
      var seen = {};
      keys.forEach(function (k) {
        var ids = byChassis[k];
        if (Array.isArray(ids)) {
          ids.forEach(function (id) {
            if (!seen[id]) { seen[id] = true; out.push(id); }
          });
        }
      });
      return out;
    });
  }

  function __resetLookupCache() { lookupPromise = null; }
  function __setLookupUrlResolver(fn) { LOOKUP_URL_FN = fn; }

  // --- URL filter sticky ---

  var FILTER_KEY = 'filter.p.m.custom.fitment_applications';
  var INTERCEPT_PATTERN = /^\/(collections\/|search(\?|$))/;

  function shouldInterceptUrl(url) {
    if (typeof url !== 'string' || !url) return false;
    // Reject absolute URLs to external origins (we only intercept same-origin nav)
    if (url.indexOf('://') !== -1) {
      try {
        var u = new URL(url);
        var myHost = root.location && root.location.host;
        if (!myHost || u.host !== myHost) return false;
        return INTERCEPT_PATTERN.test(u.pathname + u.search);
      } catch (e) {
        return false;
      }
    }
    return INTERCEPT_PATTERN.test(url);
  }

  function appendFilterToUrl(urlString, gids) {
    if (!Array.isArray(gids) || gids.length === 0) return urlString;
    var isAbsolute = urlString.indexOf('://') !== -1;
    var base = isAbsolute ? undefined : 'http://placeholder.invalid';
    var u = new URL(urlString, base);
    // Capture existing filter values to dedupe
    var existing = u.searchParams.getAll(FILTER_KEY);
    var existingSet = {};
    existing.forEach(function (v) { existingSet[v] = true; });
    gids.forEach(function (gid) {
      if (!existingSet[gid]) {
        u.searchParams.append(FILTER_KEY, gid);
        existingSet[gid] = true;
      }
    });
    if (isAbsolute) return u.toString();
    return u.pathname + (u.searchParams.toString() ? '?' + u.searchParams.toString() : '') + u.hash;
  }

  function installNavInterceptor() {
    if (!root.document || !root.document.addEventListener) return;
    root.document.addEventListener('click', function (e) {
      // Only react to plain left-click without modifier keys
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.defaultPrevented) return;
      var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
      if (!a) return;
      var href = a.getAttribute('href');
      if (!href || !shouldInterceptUrl(href)) return;
      var bmw = get();
      if (!bmw || !bmw.chassis || !bmw.engine) return;
      // Resolve gids and update href before navigation. resolveFilterIds is async,
      // so we cancel default + navigate after the promise resolves.
      e.preventDefault();
      resolveFilterIds(bmw.chassis, bmw.engine).then(function (gids) {
        if (!gids.length) {
          root.location.href = href;
          return;
        }
        root.location.href = appendFilterToUrl(href, gids);
      }, function () {
        // On lookup failure, navigate without filter rather than block the user
        root.location.href = href;
      });
    }, true);
  }

  // --- Compatibility resolver (spec §9.1.4) ---

  function matches(applicationGids, bmw, lookupData) {
    if (!Array.isArray(applicationGids) || applicationGids.length === 0) return 'unknown';
    if (!bmw || !bmw.chassis || !bmw.engine) return 'unknown';
    if (!lookupData || !lookupData.byGid) return 'unknown';

    var anyFound = false;
    var sawUnconfirmed = false;
    for (var i = 0; i < applicationGids.length; i++) {
      var app = lookupData.byGid[applicationGids[i]];
      if (!app) continue;
      anyFound = true;
      if (app.chassis_code !== bmw.chassis) continue;
      // ALL: any-Apps sind volle Treffer; motorspezifische Apps nur „unbestätigt"
      // (Chassis passt, Motor unbekannt) — Spec-Entscheidung 4.
      var engineOk = app.engine_code === 'any' ||
        (bmw.engine !== ALL_ENGINE && app.engine_code === bmw.engine);
      // Bewusst: lci/drive-Guards gelten auch für den unconfirmed-Pfad —
      // ein lci/drive-Mismatch heißt „passt sicher nicht", egal welcher Motor.
      var engineUnconfirmed = bmw.engine === ALL_ENGINE && app.engine_code !== 'any';
      if (!engineOk && !engineUnconfirmed) continue;
      // lci_state: app=any always matches; otherwise must equal bmw.lci_state (or bmw.lci_state absent = wildcard)
      if (app.lci_state && app.lci_state !== 'any' && bmw.lci_state && bmw.lci_state !== app.lci_state) continue;
      // drive: app=both always matches; otherwise must equal bmw.drive (or bmw.drive absent = wildcard)
      if (app.drive && app.drive !== 'both' && bmw.drive && bmw.drive !== app.drive) continue;
      if (engineOk) return 'match';
      sawUnconfirmed = true;
    }
    // If no GID resolved to a known app entry, we have no data — cannot determine
    if (!anyFound) return 'unknown';
    if (sawUnconfirmed) return 'engine-unconfirmed';
    return 'no-match';
  }

  // Async wrapper that resolves the lookup itself for client-side callers
  function matchesAsync(applicationGids, bmw) {
    return loadLookup().then(function (data) {
      return matches(applicationGids, bmw, data);
    });
  }

  // --- Init (runs once at script load) ---

  var initialized = false;

  function init() {
    if (initialized) return;
    initialized = true;

    // Resolve the lookup URL from the modal's data attribute if present.
    // Fallback to the CDN-relative default for resilience.
    try {
      if (root.document && root.document.getElementById) {
        var el = root.document.getElementById('t-bmw-modal');
        if (el && el.dataset && el.dataset.lookupUrl) {
          var urlStr = el.dataset.lookupUrl;
          __setLookupUrlResolver(function () { return urlStr; });
        }
      }
    } catch (e) {}

    // Read ?bmw= and hydrate
    try { hydrateFromQuery(); } catch (e) {}

    // Wire nav interceptor for sticky URL filter
    try { installNavInterceptor(); } catch (e) {}
  }

  // Gruppenschlüssel eines Taxonomie-Modellnamens — eine Regel für Gruppierung
  // (Selektor-Schritt 2), Familien-Span und formatLabel-Präfix:
  // "M4 Cabrio"→"M4", "X5 M"→"X5 M", "Z4 M Roadster"→"Z4 M", "X5"→"X5",
  // "Z4 Roadster"→"Z4", "3er Limousine"→"3er", "1er M Coupé"→"1er M",
  // sonst "Weitere".
  function modelGroupKey(model) {
    var s = model || '';
    var m = s.match(/^(M\d)/);
    if (m) return m[1];
    m = s.match(/^([XZ]\d)\s?M(\s|$)/);
    if (m) return m[1] + ' M';
    m = s.match(/^([XZ]\d)/);
    if (m) return m[1];
    m = s.match(/^(\der M)(\s|$)/);
    if (m) return m[1];
    m = s.match(/^(\d)/);
    if (m) return m[1] + 'er';
    return 'Weitere';
  }

  // Build the vehicle-pill label "<Gruppe> · Chassis · Motor",
  // z. B. "2er · F23 · N55", "X5 M · F85 · S63". Missing parts are skipped.
  function formatLabel(model, chassis, engine) {
    var parts = [];
    var key = modelGroupKey(model);
    if (key !== 'Weitere') parts.push(key);
    if (chassis) { parts.push(chassis); }
    if (engine) { parts.push(engineDisplay(engine)); }
    return parts.join(' · ');
  }

  // True, wenn ein Taxonomie-Modellname ein M-GmbH-Fahrzeug bezeichnet
  // ("M2", "M4 Cabrio"). Gleiche Regel wie formatLabel (führendes "M<Ziffer>").
  // Zentraler Ersatz für die bisher dreifach verstreuten /^M\d/-Regexe
  // (Modal, Selektor-Seite, Inline-Karte). Hinweis: würde auch ein
  // hypothetisches Modell "M135i" matchen — die kuratierte Taxonomie nutzt
  // für AG-Fahrzeuge aber nur "Xer …"-Namen, per Konvention kein Thema.
  // Ab Taxonomie v3 ist `isMEntry` mit `m`-Flag die maßgebliche Prüfung; der
  // Regex bleibt Fallback für Alt-Daten.
  function isMModel(model) {
    return /^M\d/.test(model || '');
  }

  // True für einen Taxonomie-Eintrag der M GmbH. Maßgeblich ist das explizite
  // "m": true-Flag (v3) — der Namens-Regex bleibt Fallback, weil "X5 M"/"Z4 M"
  // ihn nicht matchen und Alt-Daten (v2) kein Flag tragen.
  function isMEntry(entry) {
    if (!entry) return false;
    return entry.m === true || isMModel(entry.model);
  }

  // Findet den Taxonomie-Eintrag für einen Chassis-Code über alle Familien.
  // Ab v3 führen 19 Codes ZWEI Einträge (AG + M GmbH, z. B. E92 = „3er Coupé"
  // UND „M3 Coupé"): der reine Erst-Treffer landet immer beim AG-Eintrag. Mit
  // bekanntem Motor wird darum der Eintrag bevorzugt, dessen engines/
  // engines_diesel diesen Motor führt (M-Auswahl bleibt M). Ohne Motor — oder
  // wenn kein Eintrag ihn führt (z. B. ALL-Sentinel, Alt-Code) — gilt der erste
  // Treffer (unverändertes Alt-Verhalten). Sonderfall E82: N54 steckt in BEIDEN
  // Einträgen (135i UND 1er M Coupé) — der Motor-Treffer bleibt beim AG-Eintrag
  // (Erst-Treffer gewinnt bei Doppel-Motor; Fitment-GIDs sind identisch, nur
  // das Label unterscheidet sich). Null-sicher auf allen Argumenten.
  function findEntryForChassisEngine(families, chassis, engine) {
    if (!families || !chassis) return null;
    var first = null;
    for (var i = 0; i < families.length; i++) {
      var list = (families[i] && families[i].chassis) || [];
      for (var j = 0; j < list.length; j++) {
        var c = list[j];
        if (!c || c.code !== chassis) continue;
        if (first === null) first = c;
        if (engine) {
          var engs = (c.engines || []).concat(c.engines_diesel || []);
          if (engs.indexOf(engine) !== -1) return c;
        }
      }
    }
    return first;
  }

  // True, wenn der Pfad eine Produktdetailseite ist (/products/<handle>, auch
  // /collections/<c>/products/<handle> und mit Locale-Präfix). Bewusst URL-basiert
  // (Owner-Wunsch): steuert, dass die Fahrzeugwahl auf der PDP NICHT wegnavigiert.
  function isProductPath(pathname) {
    return /(^|\/)products\/[^/]+/.test(pathname || '');
  }

  var api = {
    STORAGE_KEY: STORAGE_KEY,
    EVENT_NAME: EVENT_NAME,
    ALL_ENGINE: ALL_ENGINE,
    engineDisplay: engineDisplay,
    get: get,
    set: set,
    clear: clear,
    formatLabel: formatLabel,
    isMModel: isMModel,
    isMEntry: isMEntry,
    modelGroupKey: modelGroupKey,
    findEntryForChassisEngine: findEntryForChassisEngine,
    isProductPath: isProductPath,
    parseBmwParam: parseBmwParam,
    serializeBmwParam: serializeBmwParam,
    hydrateFromQuery: hydrateFromQuery,
    loadLookup: loadLookup,
    resolveFilterIds: resolveFilterIds,
    __resetLookupCache: __resetLookupCache,
    __setLookupUrlResolver: __setLookupUrlResolver,
    shouldInterceptUrl: shouldInterceptUrl,
    appendFilterToUrl: appendFilterToUrl,
    installNavInterceptor: installNavInterceptor,
    matches: matches,
    matchesAsync: matchesAsync,
    init: init,
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
  if (typeof window !== 'undefined') {
    window.TrautisFitment = api;
  }
  if (typeof window !== 'undefined' && typeof root.document !== 'undefined') {
    if (root.document.readyState === 'loading') {
      root.document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  }
})(typeof window !== 'undefined' ? window : globalThis);
